import hubspotIcon from "@/assets/integrations/hubspot.svg";
import seliarisIcon from "@/assets/integrations/seliaris.png";
import { useHubSpotIntegration } from "@/contexts/hubspot-integration-provider";
import { useSeliarisIntegration } from "@/contexts/seliaris-integration-provider";
import { CrmIntegrationProviderSupported } from "@shaarp/common";
import { useMemo } from "react";

export type ConnectedCrmIntegration = {
  key: CrmIntegrationProviderSupported;
  name: string;
  logo: string;
  /**
   * The CRM refuses a contact with no email address. The send then reveals the
   * address first instead of letting the CRM answer with a validation error the
   * user cannot make sense of.
   */
  requiresContactEmail: boolean;
};

/**
 * The CRMs this team has actually connected, in a stable display order.
 *
 * The manual-send controls used to hold a one-element array literal for HubSpot;
 * reading the connected providers instead is what lets a second CRM appear
 * without touching every call site, and makes those controls disappear
 * altogether for a team connected to none.
 */
export function useConnectedCrmIntegrations(): ConnectedCrmIntegration[] {
  const { hubspotIntegration } = useHubSpotIntegration();
  const { seliarisIntegration } = useSeliarisIntegration();

  const isHubspotActive = !!hubspotIntegration?.isActive;
  const isSeliarisActive = !!seliarisIntegration?.isActive;

  return useMemo(() => {
    const connected: ConnectedCrmIntegration[] = [];
    if (isHubspotActive) {
      connected.push({
        key: "hubspot",
        name: "HubSpot",
        logo: hubspotIcon,
        requiresContactEmail: false,
      });
    }
    if (isSeliarisActive) {
      // Seliaris answers a 422 to a contact carrying no `email`.
      connected.push({
        key: "seliaris",
        name: "Seliaris",
        logo: seliarisIcon,
        requiresContactEmail: true,
      });
    }
    return connected;
  }, [isHubspotActive, isSeliarisActive]);
}
